import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _clamp from "lodash/clamp";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable consistent-return */


import React, { useRef, useState, useMemo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { theme, whenFocused } from "../../theme";
import { motion, useMotionValue, animate as animateMotionValue } from 'framer-motion';
import { easeCurves, eases } from "../../motion/eases";
import { transparentize } from 'polished';
import { TooltipContent } from "../Tooltip";
import { useActiveState, useForkedRef, useIsomorphicLayoutEffect, useMouseMetrics } from "../../hooks";
import { isFunction } from "../../utils/is-function";
import { valueToPercent, percentToValue, roundValueToStep } from "./slider-utils";
import { MarkIndicator, MarkLabel } from "./Mark";
var defaultAnimationVariants = {
  container: {
    showing: {},
    hidden: {}
  },
  marks: {
    showing: {},
    hidden: {}
  },
  labels: {
    showing: {},
    hidden: {}
  }
};
export var Slider = React.forwardRef(function (_ref, forwardedRef) {
  var value = _ref.value,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$continuous = _ref.continuous,
      continuous = _ref$continuous === void 0 ? true : _ref$continuous,
      _ref$min = _ref.min,
      min = _ref$min === void 0 ? 0 : _ref$min,
      _ref$max = _ref.max,
      max = _ref$max === void 0 ? 100 : _ref$max,
      _ref$step = _ref.step,
      step = _ref$step === void 0 ? 1 : _ref$step,
      _ref$marks = _ref.marks,
      marks = _ref$marks === void 0 ? [] : _ref$marks,
      _ref$showTooltip = _ref.showTooltip,
      showTooltip = _ref$showTooltip === void 0 ? true : _ref$showTooltip,
      _ref$showHoverIndicat = _ref.showHoverIndicator,
      showHoverIndicator = _ref$showHoverIndicat === void 0 ? true : _ref$showHoverIndicat,
      onChange = _ref.onChange,
      _ref$trackSize = _ref.trackSize,
      trackSize = _ref$trackSize === void 0 ? 4 : _ref$trackSize,
      _ref$thumbSize = _ref.thumbSize,
      thumbSize = _ref$thumbSize === void 0 ? 12 : _ref$thumbSize,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 0 : _ref$offset,
      _ref$trackColor = _ref.trackColor,
      trackColor = _ref$trackColor === void 0 ? 'brandPrimary' : _ref$trackColor,
      _ref$animationVariant = _ref.animationVariants,
      animationVariants = _ref$animationVariant === void 0 ? defaultAnimationVariants : _ref$animationVariant,
      style = _ref.style;
  /** Interactive states */

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hovering = _useState2[0],
      setHovering = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      dragging = _useState4[0],
      setDragging = _useState4[1];

  var _useActiveState = useActiveState(),
      thumbActive = _useActiveState.isActive,
      bindThumbActive = _useActiveState.bind;
  /** Refs */


  var thumbRef = useRef(null);
  var ref = useForkedRef(forwardedRef, thumbRef);
  var boundsRef = useRef(null);
  var containerRef = useRef(null);
  /** Imperative animations */

  var hoverX = useMotionValue(null);
  var thumbX = useMotionValue(null);
  /**
   * Whether we are rendering labels.
   * Note: this only actually memoizes if marks is an object
   * which itself is memoized, or is defined outside of the react
   * render lifecycle.
   */

  var hasLabels = useMemo(function () {
    return marks.some(function (mark) {
      return mark.label;
    });
  }, [marks]);
  /**
   * Focus helper
   */

  function focusSlider() {
    thumbRef.current.focus();
  }
  /**
   * Handle left / right keyboard navigation
   */


  function handleKeyDown(e) {
    var newValue = value;

    function getNextStep() {
      var stepValue = min;

      while (stepValue <= max) {
        if (stepValue > value) {
          newValue = stepValue;
          break;
        }

        stepValue = Number((stepValue + step).toFixed(2));
      }
    }

    function getPrevStep() {
      var stepValue = max;

      while (stepValue >= min) {
        if (stepValue < value) {
          newValue = stepValue;
          break;
        }

        stepValue = Number((stepValue - step).toFixed(2));
      }
    }

    switch (e.key) {
      case 'Home':
        newValue = min;
        break;

      case 'End':
        newValue = max;
        break;

      case 'PageUp':
      case 'ArrowRight':
        e.stopPropagation();
        getNextStep();
        break;

      case 'PageDown':
      case 'ArrowLeft':
        e.stopPropagation();
        getPrevStep();
        break;

      default:
        return;
    }

    e.preventDefault();
    onChange(_clamp(newValue, min, max));
  }
  /**
   * Handle start of panning
   */


  function onPanStart() {
    setDragging(true);
    hoverX.set(0);
  }
  /**
   * Handle panning
   */


  function onPan(_, _ref2) {
    var x = _ref2.point.x;
    var _boundsRef$current = boundsRef.current,
        left = _boundsRef$current.left,
        width = _boundsRef$current.width;
    var newPercentage = (x - left) / width;
    var newValue = percentToValue(newPercentage, min, max);

    if (step && !continuous) {
      newValue = roundValueToStep(newValue, step, min);
    }

    onChange(_clamp(newValue, min, max));
  }
  /**
   * Handle end of panning
   */


  function onPanEnd() {
    setDragging(false);
  }
  /**
   * When a user clicks anywhere in the slider,
   * skip to that point.
   */


  function onTap(_, info) {
    onPan(null, info);
    focusSlider();
  }
  /*Update our hover indicator position */


  var updateHoverPosition = useCallback(function (percentage) {
    var v = _clamp(percentage * boundsRef.current.width, 0, boundsRef.current.width - 2);

    hoverX.set(v);
  }, [hoverX]);
  /**
   * Determine ghost hover indicator logic, including potential
   * "gravity" of marks
   */

  function handleMouseMove(info) {
    if (!hovering) {
      if (!dragging) {
        setHovering(true);
      }
    }

    var _boundsRef$current2 = boundsRef.current,
        left = _boundsRef$current2.left,
        width = _boundsRef$current2.width; // find ghost percentage

    var targetPercentage = (info.xy[0] - left) / width; // Otherwise, we immediately update our ghost indicator

    updateHoverPosition(targetPercentage);
  }
  /**
   * Handle mouse enter for ghosts
   */


  function onMouseEnter() {
    if (dragging) {
      return;
    }

    if (!hovering) {
      setHovering(true);
    }
  }
  /**
   * Handle mouseleave
   */


  function onMouseLeave() {
    setHovering(false);
    hoverX.set(null);
  }

  var _useMouseMetrics = useMouseMetrics({
    onMouseMove: handleMouseMove,
    onMouseLeave: onMouseLeave
  }),
      bind = _useMouseMetrics.bind;
  /**
   * Set / Animate between value changes
   */


  useIsomorphicLayoutEffect(function () {
    var percent = valueToPercent(value, min, max);
    var thumbXValue = "-".concat(100 - percent, "%");

    if (dragging || thumbX.get() == null) {
      thumbX.set(thumbXValue);
    } else {
      animateMotionValue(thumbX, thumbXValue, {
        duration: 0.2,
        ease: eases.primary
      });
    }

    return function () {
      return thumbX.stop();
    };
  }, [dragging, max, min, thumbX, value]);
  var hoverFunctions = showHoverIndicator && continuous ? _objectSpread({
    onMouseEnter: onMouseEnter
  }, bind) : {};

  function getContainerBounds() {
    boundsRef.current = containerRef.current.getBoundingClientRect();
  }

  return /*#__PURE__*/React.createElement(OuterContainer, {
    "data-testid": "slider-container",
    $hasLabels: hasLabels,
    $trackSize: trackSize,
    $thumbSize: thumbSize,
    $trackColor: trackColor,
    $disabled: disabled,
    $offset: offset,
    onMouseEnter: getContainerBounds,
    style: style
  }, /*#__PURE__*/React.createElement(Container, {
    ref: containerRef
  }, hasLabels && /*#__PURE__*/React.createElement(LabelContainer, {
    variants: animationVariants.container,
    initial: "hidden",
    animate: "showing",
    exit: "hidden"
  }, marks.map(function (mark) {
    function onTapStart() {
      onChange(mark.value);
      thumbRef.current.focus();
    }

    return /*#__PURE__*/React.createElement(MarkLabel, {
      key: mark.value,
      mark: mark,
      min: min,
      max: max,
      value: value,
      onTapStart: onTapStart,
      fade: dragging || thumbActive,
      animationVariants: animationVariants.marks
    });
  })), /*#__PURE__*/React.createElement(RelativeContainer, null, /*#__PURE__*/React.createElement(AnimatedContainer, _extends({
    variants: animationVariants.container,
    initial: "hidden",
    animate: "showing",
    exit: "hidden",
    onMouseDown: function onMouseDown(e) {
      return e.preventDefault();
    },
    onTapStart: onTap,
    onPanStart: onPanStart,
    onPan: onPan,
    onPanEnd: onPanEnd
  }, hoverFunctions), /*#__PURE__*/React.createElement(Rail, {
    $offset: offset
  }, /*#__PURE__*/React.createElement(Track, {
    style: {
      x: thumbX
    }
  }), showHoverIndicator && continuous && /*#__PURE__*/React.createElement(HoverTarget, {
    $visible: !dragging && hovering,
    style: {
      x: hoverX
    }
  }), marks.map(function (mark) {
    return /*#__PURE__*/React.createElement(MarkIndicator, {
      key: mark.value,
      mark: mark,
      min: min,
      max: max,
      value: value,
      animationVariants: animationVariants.marks
    });
  })), /*#__PURE__*/React.createElement("input", {
    value: String(value),
    type: "hidden"
  }), /*#__PURE__*/React.createElement(ThumbContainer, {
    style: {
      x: thumbX
    }
  }, /*#__PURE__*/React.createElement(Thumb, _extends({
    ref: ref,
    disabled: disabled,
    tabIndex: disabled ? null : 0,
    "aria-valuemax": max,
    "aria-valuemin": min,
    "aria-valuenow": value,
    role: "slider",
    onKeyDown: handleKeyDown
  }, bindThumbActive), showTooltip && /*#__PURE__*/React.createElement(TooltipPosition, null, /*#__PURE__*/React.createElement(TrackingTooltip, {
    "aria-hidden": "true",
    role: "tooltip",
    id: "slider-tooltip",
    placement: "top",
    title: isFunction(showTooltip) ? showTooltip(value) : value.toFixed(),
    isShowing: dragging || thumbActive
  }))))))));
});
var OuterContainer = styled(motion.div).withConfig({
  displayName: "Slider__OuterContainer",
  componentId: "vapor__sc-1p1du0i-0"
})(["--fio-slider-tracksize:", ";--fio-slider-thumbsize:", ";--fio-track-color:", ";--fio-slider-offset:", ";padding-left:var(--fio-slider-offset);padding-right:var(--fio-slider-offset);min-height:var(--fio-slider-thumbsize);display:flex;align-items:center;user-select:none;", ""], function (p) {
  return theme.spaces[p.$trackSize];
}, function (p) {
  return theme.spaces[p.$thumbSize];
}, function (p) {
  return !p.$offset ? theme.colors.palette[p.$trackColor] : 'transparent';
}, function (p) {
  return theme.spaces[p.$offset];
}, function (p) {
  return p.$disabled && "\n    pointer-events: none;\n    cursor: default;\n    filter: grayscale(100%);\n    opacity: 0.5;\n  ";
});
var RelativeContainer = styled.div.withConfig({
  displayName: "Slider__RelativeContainer",
  componentId: "vapor__sc-1p1du0i-1"
})(["position:relative;"]);
var HoverTarget = styled(motion.div).withConfig({
  displayName: "Slider__HoverTarget",
  componentId: "vapor__sc-1p1du0i-2"
})(["width:2px;height:var(--fio-slider-tracksize);position:absolute;background:", ";transition:opacity 0.4s ", ";opacity:", ";"], theme.colors.palette.whiteA15, easeCurves.primary, function (p) {
  return p.$visible ? 1 : 0;
});
var Container = styled(motion.div).withConfig({
  displayName: "Slider__Container",
  componentId: "vapor__sc-1p1du0i-3"
})(["width:100%;position:relative;touch-action:none;color:black;-webkit-tap-highlight-color:transparent;&[disabled]{pointer-events:none;}"]);
var AnimatedContainer = styled(motion.div).withConfig({
  displayName: "Slider__AnimatedContainer",
  componentId: "vapor__sc-1p1du0i-4"
})(["height:var(--fio-slider-tracksize);&:before{top:-", ";bottom:-", ";left:calc(var(--fio-slider-offset) * -1);right:calc(var(--fio-slider-offset) * -1);content:'';position:absolute;min-width:100%;}"], theme.spaces[8], theme.spaces[8]);
var Rail = styled(motion.span).withConfig({
  displayName: "Slider__Rail",
  componentId: "vapor__sc-1p1du0i-5"
})(["display:block;width:100%;height:var(--fio-slider-tracksize);background-color:", ";border-radius:10px;position:absolute;z-index:", ";", ""], theme.colors.palette.whiteA15, theme.zIndices.base, function (p) {
  if (p.$offset) {
    return css(["border-radius:0;&:before{position:absolute;content:'';border-top-left-radius:10px;border-bottom-left-radius:10px;height:var(--fio-slider-tracksize);width:var(--fio-slider-offset);background-color:", ";right:100%;}&:after{position:absolute;content:'';border-top-right-radius:10px;border-bottom-right-radius:10px;height:var(--fio-slider-tracksize);width:var(--fio-slider-offset);background-color:", ";left:100%;}"], theme.colors.palette.whiteA15, theme.colors.palette.whiteA15);
  }

  return css(["border-radius:10px;overflow:hidden;"]);
});
var Track = styled(motion.span).withConfig({
  displayName: "Slider__Track",
  componentId: "vapor__sc-1p1du0i-6"
})(["height:var(--fio-slider-tracksize);display:block;position:absolute;border-radius:", ";width:100%;transform-origin:top left;background-color:var(--fio-track-color);"], theme.radii[8]);
var ThumbContainer = styled(motion.div).withConfig({
  displayName: "Slider__ThumbContainer",
  componentId: "vapor__sc-1p1du0i-7"
})(["width:100%;position:absolute;z-index:", ";"], theme.zIndices.base + 1);
var Thumb = styled(motion.span).withConfig({
  displayName: "Slider__Thumb",
  componentId: "vapor__sc-1p1du0i-8"
})(["width:var(--fio-slider-thumbsize);height:var(--fio-slider-thumbsize);display:block;outline:0;position:absolute;align-items:center;border-radius:50%;justify-content:center;background-color:", ";box-shadow:0 1px 4px rgba(0,0,0,0.3);transition:box-shadow 0.15s ", ";user-select:none;right:0;transform:translateX(50%) translateY(calc(-50% + var(--fio-slider-tracksize) / 2));", "{box-shadow:0 0 0 4px ", ";}"], theme.colors.palette.grey90, easeCurves.primary, whenFocused, transparentize(0.4, theme.colors.palette.brandPrimary));
var TrackingTooltip = styled(TooltipContent).withConfig({
  displayName: "Slider__TrackingTooltip",
  componentId: "vapor__sc-1p1du0i-9"
})(["width:auto;margin-bottom:", ";user-select:none;font-variant-numeric:tabular-nums;"], theme.spaces[8]);
var TooltipPosition = styled.div.withConfig({
  displayName: "Slider__TooltipPosition",
  componentId: "vapor__sc-1p1du0i-10"
})(["pointer-events:none;position:absolute;left:50%;bottom:100%;transform:translateX(-50%);"]);
var LabelContainer = styled(motion.div).withConfig({
  displayName: "Slider__LabelContainer",
  componentId: "vapor__sc-1p1du0i-11"
})(["position:relative;height:32px;"]);